/**
 * color.ts — dependency-free WCAG contrast helpers.
 *
 * The cover background is the admin's `primary` color, which can be anything
 * from near-black to pastel. Text and the headline accent must stay readable
 * regardless. These helpers derive a high-contrast foreground from the
 * background luminance instead of hardcoding white (which fails on light
 * backgrounds) or trusting `accent` (which fails when it's close to primary).
 */

function linearize(channel: number): number {
  const s = channel / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance of a #rrggbb hex color. */
export function luminance(hex: string): number {
  const r = linearize(parseInt(hex.slice(1, 3), 16));
  const g = linearize(parseInt(hex.slice(3, 5), 16));
  const b = linearize(parseInt(hex.slice(5, 7), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two #rrggbb hex colors (1..21). */
export function contrastRatio(a: string, b: string): number {
  const l1 = Math.max(luminance(a), luminance(b));
  const l2 = Math.min(luminance(a), luminance(b));
  return (l1 + 0.05) / (l2 + 0.05);
}

/** Pick whichever of light/dark has the higher contrast against `bg`. */
export function autoContrast(bg: string, light = "#ffffff", dark = "#111111"): string {
  return contrastRatio(light, bg) >= contrastRatio(dark, bg) ? light : dark;
}

/**
 * Choose the brand logo variant that suits a surface: a dark background wants
 * the dark-surface logo (usually light/white), a light background wants the
 * light-surface logo (usually dark). Falls back to the other variant when only
 * one is provided, and to "" when neither is.
 */
export function pickLogoForBackground(
  bg: string,
  logoForDarkSurface: string,
  logoForLightSurface: string,
): string {
  const surfaceIsDark = luminance(bg) < 0.5;
  const preferred = surfaceIsDark ? logoForDarkSurface : logoForLightSurface;
  const fallback = surfaceIsDark ? logoForLightSurface : logoForDarkSurface;
  return preferred || fallback || "";
}

function toByte(n: number): string {
  return Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
}

/** Blend two #rrggbb colors; t=0 → a, t=1 → b. */
export function mix(a: string, b: string, t: number): string {
  const ch = (h: string, i: number) => parseInt(h.slice(1 + i * 2, 3 + i * 2), 16);
  return (
    "#" +
    toByte(ch(a, 0) + (ch(b, 0) - ch(a, 0)) * t) +
    toByte(ch(a, 1) + (ch(b, 1) - ch(a, 1)) * t) +
    toByte(ch(a, 2) + (ch(b, 2) - ch(a, 2)) * t)
  );
}

/**
 * Subtle table border/divider color derived from the page background: a faint
 * nudge toward the background's contrast color so the line is always visible
 * (never blends into the page) but stays light. Replaces the hardcoded gray so
 * every table reacts to the background instead of assuming a white page.
 */
export function subtleBorder(bg: string, strength = 0.18): string {
  return mix(bg, autoContrast(bg), strength);
}

/**
 * Zebra (alternating row) fill tinted by the brand: a faint blend of the page
 * background toward `brand`. When the brand color is too close to the
 * background to tint visibly (e.g. white primary on a white page) it falls back
 * to a neutral so the stripe never vanishes. `strength` controls the apparent
 * transparency — higher = more saturated stripe.
 */
export function zebraFill(bg: string, brand: string, strength = 0.08): string {
  const tint = contrastRatio(brand, bg) >= 1.5 ? brand : autoContrast(bg);
  return mix(bg, tint, strength);
}

/**
 * Use `preferred` (a brand color) as text on `bg` only when it stays readable;
 * otherwise fall back to a safe neutral. For brand colors painted as text on a
 * light page that would wash out when the admin picks a pale brand color.
 */
export function readableOn(bg: string, preferred: string, minRatio = 4.5): string {
  return contrastRatio(preferred, bg) >= minRatio ? preferred : autoContrast(bg);
}

/** Large bold headline minimum (WCAG AA large-text bar). */
const LARGE_TEXT_MIN_RATIO = 3.0;

/**
 * Use `accent` for the headline only when it clears the large-text contrast
 * bar against the background; otherwise fall back to a safe neutral. Keeps the
 * branded accent-as-headline look when it works, degrades gracefully when not.
 */
export function safeTitleColor(bg: string, accent: string): string {
  return contrastRatio(accent, bg) >= LARGE_TEXT_MIN_RATIO ? accent : autoContrast(bg);
}
